import { ValidationHookReturn, ValidationState } from '@/src/utils/itinerary/travelerInputValidationRules'
import { useCallback, useMemo, useState } from 'react'

interface UseValidationConfig {
    initialValidationState: ValidationState
}

const useValidation = ({ initialValidationState }: UseValidationConfig): ValidationHookReturn => {
    const [validationState, setValidationState] = useState<ValidationState>(initialValidationState)

    // Validate a single field
    const validateField = useCallback(
        (fieldName: string, value: any, allValues?: Record<string, any>) => {
            const fieldConfig = validationState[fieldName]
            if (!fieldConfig) return false

            let errorMessage = ''

            // Run all validation rules
            for (const rule of fieldConfig.rules) {
                const result = rule(value, allValues)
                if (result) {
                    errorMessage = result
                    break // Stop at first error
                }
            }

            setValidationState(prev => ({
                ...prev,
                [fieldName]: {
                    ...prev[fieldName],
                    isError: !!errorMessage,
                    errorMessage,
                    touched: true
                }
            }))

            return !errorMessage
        },
        [validationState]
    )

    // Validate all fields
    const validateAll = useCallback(
        (values: Record<string, any>): boolean => {
            let hasErrors = false
            const newValidationState = { ...validationState }

            Object.keys(values).forEach(fieldName => {
                const fieldConfig = validationState[fieldName]
                const value = values[fieldName]
                let errorMessage = ''

                // Run all validation rules
                for (const rule of fieldConfig.rules) {
                    const result = rule(value, values)
                    if (result) {
                        errorMessage = result
                        break
                    }
                }

                newValidationState[fieldName] = {
                    ...fieldConfig,
                    isError: !!errorMessage,
                    errorMessage,
                    touched: true
                }

                if (errorMessage) {
                    hasErrors = true
                }
            })

            setValidationState(newValidationState)
            return !hasErrors
        },
        [validationState]
    )

    // Clear error for a specific field
    const clearFieldError = useCallback((fieldName: string) => {
        setValidationState(prev => ({
            ...prev,
            [fieldName]: {
                ...prev[fieldName],
                isError: false,
                errorMessage: ''
            }
        }))
    }, [])

    // Clear all errors
    const clearAllErrors = useCallback(() => {
        const clearedState: ValidationState = {}
        Object.keys(validationState).forEach(fieldName => {
            clearedState[fieldName] = {
                ...validationState[fieldName],
                isError: false,
                errorMessage: ''
            }
        })
        setValidationState(clearedState)
    }, [validationState])

    // Set field as touched
    const setFieldTouched = useCallback((fieldName: string, touched: boolean = true) => {
        setValidationState(prev => ({
            ...prev,
            [fieldName]: {
                ...prev[fieldName],
                touched
            }
        }))
    }, [])

    // Computed properties
    const hasErrors = useMemo(() => {
        return Object.values(validationState).some(field => field.isError)
    }, [validationState])

    const isValid = useMemo(() => {
        return Object.values(validationState).every(field => !field.isError && field.touched)
    }, [validationState])

    return {
        validationState,
        validateField,
        validateAll,
        clearFieldError,
        clearAllErrors,
        setFieldTouched,
        hasErrors,
        isValid
    }
}

export default useValidation
